import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Info, Megaphone, AlertTriangle, X, ShieldAlert } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DISMISSED_KEY = 'allure_dismissed_announcements';

const TYPE_STYLES = {
  info: { bg: '#1F2945', color: '#EAF2FF', icon: Info, accent: '#5C8DFF' },
  promo: { bg: 'linear-gradient(135deg, #5B2EFF 0%, #C53AFF 100%)', color: '#FFFFFF', icon: Megaphone, accent: '#FFFFFF' },
  warn: { bg: '#3D2A12', color: '#FFE8CC', icon: AlertTriangle, accent: '#FFB454' },
  critical: { bg: '#3A1318', color: '#FFE3E6', icon: ShieldAlert, accent: '#FF5566' },
};

const readDismissed = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]'));
  } catch {
    return new Set();
  }
};

const persistDismissed = (set) => {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* noop */
  }
};

export const AnnouncementBanner = () => {
  const navigate = useNavigate();
  const [announcement, setAnnouncement] = useState(null);
  const [dismissed, setDismissed] = useState(() => readDismissed());

  const fetchActive = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAnnouncement(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/announcements/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(res.data) ? res.data : [];
      const next = list.find((row) => !dismissed.has(row.id)) || null;
      setAnnouncement(next);
    } catch {
      setAnnouncement(null);
    }
  }, [dismissed]);

  useEffect(() => {
    fetchActive();
    const onStorage = (event) => {
      if (event.key === 'token' || event.key === null) fetchActive();
    };
    const onCustom = () => fetchActive();
    window.addEventListener('storage', onStorage);
    window.addEventListener('allure:auth-changed', onCustom);
    window.addEventListener('allure:announcement-changed', onCustom);
    // Poll every 2 minutes as a safety net (Socket.IO may not be connected on every page)
    const interval = setInterval(fetchActive, 120_000);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('allure:auth-changed', onCustom);
      window.removeEventListener('allure:announcement-changed', onCustom);
      clearInterval(interval);
    };
  }, [fetchActive]);

  const styles = useMemo(() => {
    if (!announcement) return null;
    return TYPE_STYLES[announcement.type] || TYPE_STYLES.info;
  }, [announcement]);

  const handleDismiss = useCallback(() => {
    if (!announcement) return;
    const next = new Set(dismissed);
    next.add(announcement.id);
    setDismissed(next);
    persistDismissed(next);
    setAnnouncement(null);
  }, [announcement, dismissed]);

  const handleCta = useCallback(() => {
    if (!announcement?.cta_url) return;
    if (announcement.cta_url.startsWith('http')) {
      window.open(announcement.cta_url, '_blank', 'noopener');
    } else {
      window.location.assign(announcement.cta_url);
    }
  }, [announcement]);

  if (!announcement || !styles) return null;
  const Icon = styles.icon;

  return (
    <div
      data-testid="announcement-banner"
      role="status"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10400,
        background: styles.bg,
        color: styles.color,
        // paddingTop extends under the status bar (native-app pattern):
        // background fills the notch/status-bar area, content starts below it.
        padding: 'calc(env(safe-area-inset-top, 0px) + 10px) 16px 10px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontFamily: "'Manrope', system-ui, sans-serif",
        boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Icon size={17} color={styles.accent} strokeWidth={2.2} />
      <span
        data-testid="announcement-banner-text"
        style={{ flex: 1, fontSize: 13.5, lineHeight: 1.35, fontWeight: 500, letterSpacing: 0.1 }}
      >
        {announcement.text}
      </span>
      {announcement.cta_label && announcement.cta_url ? (
        <button
          type="button"
          onClick={handleCta}
          data-testid="announcement-banner-cta"
          style={{
            background: 'rgba(255,255,255,0.15)',
            color: styles.color,
            border: `1px solid ${styles.accent}`,
            borderRadius: 999,
            padding: '5px 12px',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            letterSpacing: 0.2,
          }}
        >
          {announcement.cta_label}
        </button>
      ) : null}
      {announcement.dismissible ? (
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="announcement-banner-dismiss"
          aria-label="Dismiss"
          style={{
            background: 'rgba(255,255,255,0.10)',
            border: 'none',
            color: styles.color,
            borderRadius: 999,
            padding: 5,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={14} />
        </button>
      ) : null}
    </div>
  );
};

export default AnnouncementBanner;
